import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { CAMPAIGN_MEMBER_SEGMENTS } from "@demore/shared/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api, ApiError } from "@/lib/api";
import { useWorkspace, useWorkspaceCan } from "./WorkspaceContext";

type Member = {
  id: string;
  subjectType: "user" | "viewer";
  subjectName: string | null;
  subjectEmail: string | null;
  segment: string | null;
  // Option A (#2607): the campaign owner appears as a synthesized Campaign
  // CEO row — not an assignment, so it can't be re-segmented or removed.
  isOwner?: boolean;
  // #2624: a disabled teammate keeps their grant row but loses access at
  // access time. Surface it so the admin can remove the stale grant.
  subjectStatus?: string | null;
};

export function TeamTab() {
  const { campaignId, campaign } = useWorkspace();
  // #2504-2506: only super_admin / owning campaign_owner / people_ops manage
  // members; every other role gets a read-only roster.
  const { manageTeam } = useWorkspaceCan();
  const qc = useQueryClient();
  const base = `/api/admin/campaign-assignments/${campaignId}`;
  const [email, setEmail] = useState("");
  const [segment, setSegment] = useState("");

  const { data } = useQuery({
    queryKey: ["team", campaignId],
    queryFn: () => api.get<{ items: Member[] }>(base),
  });

  const grant = useMutation({
    mutationFn: () => api.post(base, { email, segment: segment || null }),
    onSuccess: () => {
      toast.success("Member added");
      setEmail("");
      setSegment("");
      qc.invalidateQueries({ queryKey: ["team", campaignId] });
      qc.invalidateQueries({ queryKey: ["audience", campaignId] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 404)
        // QA feedback (Saba, 2026-07-03): say what to DO, not just what's wrong.
        toast.error(
          "No user or past viewer with that email. Send them a Demo Pack first (opening it makes them a viewer), or invite them under Users.",
          { duration: 8000 },
        );
      // 409 now covers more than "already assigned" — e.g. #2624's "that user
      // is disabled and would have no access; enable them first". Show the
      // server's actionable copy instead of hardcoding one guess.
      else if (err instanceof ApiError && err.status === 409)
        toast.error(err.message || "Already a member", { duration: 8000 });
      else toast.error(err instanceof Error ? err.message : "Add failed");
    },
  });

  const setSeg = useMutation({
    mutationFn: (v: { id: string; segment: string }) =>
      api.patch(`${base}/${v.id}`, { segment: v.segment || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team", campaignId] });
      qc.invalidateQueries({ queryKey: ["audience", campaignId] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Update failed"),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`${base}/${id}`),
    onSuccess: () => {
      toast.success("Member removed");
      qc.invalidateQueries({ queryKey: ["team", campaignId] });
      qc.invalidateQueries({ queryKey: ["audience", campaignId] });
    },
  });

  const members = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-head text-lg font-semibold">Team</h2>
        <p className="text-xs text-muted-foreground">
          Members of {campaign.name}. A grant unlocks this campaign's campaign-assigned media; the
          segment drives the Overview audience cards.
        </p>
      </div>

      {manageTeam && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-2 items-end">
            <Input
              className="flex-1 min-w-[200px]"
              type="email"
              placeholder="email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") grant.mutate();
              }}
            />
            <Select value={segment} onChange={(e) => setSegment(e.target.value)}>
              <option value="">No segment</option>
              {CAMPAIGN_MEMBER_SEGMENTS.map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
              ))}
            </Select>
            <Button onClick={() => grant.mutate()} disabled={!email || grant.isPending}>
              {grant.isPending ? "Adding…" : "Add member"}
            </Button>
          </div>
        </Card>
      )}

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-admin-rule">
              <th className="p-3 font-medium">Member</th>
              <th className="p-3 font-medium">Type</th>
              <th className="p-3 font-medium">Segment</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-b border-admin-rule">
                <td className="p-3">
                  <div className="font-medium">
                    {m.subjectName ?? m.subjectEmail ?? "(unknown)"}
                    {m.subjectStatus === "disabled" && (
                      <Badge
                        variant="danger"
                        className="ml-2"
                        title="This teammate is disabled. Their access is already denied, but the grant row remains — remove it to tidy up."
                      >
                        disabled — no access
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{m.subjectEmail}</div>
                </td>
                <td className="p-3">
                  <Badge variant="muted">{m.isOwner ? "owner" : m.subjectType}</Badge>
                </td>
                <td className="p-3">
                  {manageTeam && !m.isOwner ? (
                    <Select
                      value={m.segment ?? ""}
                      onChange={(e) => setSeg.mutate({ id: m.id, segment: e.target.value })}
                    >
                      <option value="">— none —</option>
                      {CAMPAIGN_MEMBER_SEGMENTS.map((s) => (
                        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                      ))}
                    </Select>
                  ) : (
                    <Badge variant="outline">{(m.segment ?? "—").replace(/_/g, " ")}</Badge>
                  )}
                </td>
                <td className="p-3 text-right">
                  {manageTeam && !m.isOwner && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => revoke.mutate(m.id)}
                      aria-label="Remove member"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {members.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-xs text-muted-foreground">
                  No members yet. Add someone by email above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
